// Shared attribution helpers for interview-sourced blog posts.
// Browser-safe: used by admin UI, the blog view, and schema builders.
import { SITE_URL } from "@/lib/seo";

export type Credit = {
  name: string;
  role?: string | null;
  person_id?: string | null;
};

export const TEAM_NAME = "Durafoam Roofing Team";

/** Canonical schema.org @id for the people we have identity pages for. */
export const CANONICAL_PERSON_IDS: Record<string, string> = {
  "Tim Forstie": `${SITE_URL}/tim-forstie#person`,
  "Curtis Forstie": `${SITE_URL}/about-us#curtis-forstie`,
  "Ben Forstie": `${SITE_URL}/about-us#ben-forstie`,
};

export const ATTRIBUTION_MODES = [
  { value: "decide_at_review", label: "Decide during final review (recommended)" },
  { value: "respondent_author", label: "Respondent is the author" },
  { value: "staff_author", label: "Staff member is the author; respondent credited as contributor" },
  { value: "team_author", label: "Durafoam Roofing Team is the author; respondent credited as contributor" },
  { value: "multiple_authors", label: "Multiple authors" },
] as const;

export const LENGTH_MODES = [
  { value: "quick", label: "Quick Interview — 3 questions", count: 3 },
  { value: "standard", label: "Standard Interview — 5 questions", count: 5 },
  { value: "custom", label: "Custom number", count: 5 },
] as const;

export function lengthToCount(mode: string, custom?: number | null): number {
  if (mode === "quick") return 3;
  if (mode === "custom") return Math.max(1, Math.min(12, Math.round(custom ?? 5)));
  return 5;
}

function withRole(c: Credit): string {
  return c.role ? `${c.name}, ${c.role}` : c.name;
}

function joinNames(list: string[]): string {
  if (list.length <= 1) return list[0] ?? "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/**
 * Public-facing byline, e.g.
 *   By Ben Forstie — With insights from Curtis Forstie, Owner of Durafoam Roofing
 */
export function attributionLine(authors: Credit[], contributors: Credit[] = []): string {
  const authorNames = authors.map((a) => a.name).filter(Boolean);
  const isTeam = authorNames.length === 1 && authorNames[0] === TEAM_NAME;
  const authorPart = authorNames.length
    ? `By ${isTeam ? `the ${TEAM_NAME}` : joinNames(authorNames)}`
    : `By the ${TEAM_NAME}`;

  const authorSet = new Set(authorNames);
  const credits = contributors.filter((c) => c.name && !authorSet.has(c.name));
  if (credits.length === 0) {
    // Single author with a role reads better as "By Tim Forstie — Owner, Durafoam Roofing"
    if (authors.length === 1 && authors[0].role && !isTeam) {
      return `${authorPart} — ${authors[0].role}`;
    }
    return authorPart;
  }
  if (credits.length === 1) {
    return `${authorPart} — With insights from ${withRole(credits[0])}`;
  }
  return `${authorPart} — Featuring insights from ${joinNames(credits.map(withRole))}`;
}

/** schema.org node for one credit — Person, or Organization for the team byline. */
export function creditNode(c: Credit) {
  if (!c.name || c.name === TEAM_NAME) {
    return { "@type": "Organization", name: "Durafoam Roofing", "@id": `${SITE_URL}/#business` };
  }
  const id = CANONICAL_PERSON_IDS[c.name];
  const node: Record<string, unknown> = { "@type": "Person", name: c.name };
  if (id) node["@id"] = id;
  if (c.role) node.jobTitle = c.role;
  return node;
}

/** Each credit stays its own node — never merged into one name. */
export function creditNodes(list: Credit[]) {
  const nodes = list.filter((c) => c?.name).map(creditNode);
  return nodes.length ? nodes : undefined;
}

export function normalizeCredits(raw: unknown): Credit[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c) => c && typeof c === "object" && typeof (c as any).name === "string")
    .map((c: any) => ({
      name: String(c.name).trim(),
      role: c.role ? String(c.role) : null,
      person_id: c.person_id ?? null,
    }))
    .filter((c) => c.name.length > 0);
}

/** Rough "is this FAQ question basically the interview question?" check. */
export function isNearDuplicate(a: string, b: string): boolean {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 3);
  const A = new Set(norm(a));
  const B = new Set(norm(b));
  if (A.size === 0 || B.size === 0) return false;
  let shared = 0;
  for (const w of A) if (B.has(w)) shared++;
  return shared / Math.min(A.size, B.size) >= 0.7;
}
